// @givmo/social — a post being written, kept in this tab while its writer reconnects an account
// (SMM-WEB1; the 2026-11-03 user test, U7). A post that can be saved is saved as a draft first; one
// that can't yet (an Instagram account chosen with no image, say) is kept here, per organization, and
// the composer picks it up when the person comes back. Never sent anywhere; cleared once read.

import type { ComposerState } from './composer-model';

const KEY = 'givmo.social.compose-stash';

function store(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function stashComposer(orgRef: string, state: ComposerState): void {
  try {
    store()?.setItem(`${KEY}.${orgRef}`, JSON.stringify(state));
  } catch {
    // Storage refused: the person writes it again.
  }
}

/** The kept post, or null (read without taking it: a state initializer may run twice). */
export function readStashedComposer(orgRef: string | null): ComposerState | null {
  if (orgRef === null) return null;
  try {
    const raw = store()?.getItem(`${KEY}.${orgRef}`) ?? null;
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<ComposerState>;
    if (typeof value.text !== 'string' || !Array.isArray(value.channels)) return null;
    return {
      text: value.text,
      link: typeof value.link === 'string' ? value.link : '',
      image: value.image ?? null,
      channels: value.channels.filter((entry): entry is string => typeof entry === 'string'),
      overrides:
        value.overrides && typeof value.overrides === 'object' ? { ...value.overrides } : {},
      context: value.context ?? null,
    };
  } catch {
    return null;
  }
}

/** Forget the kept post once the composer has it. */
export function clearStashedComposer(orgRef: string | null): void {
  if (orgRef === null) return;
  try {
    store()?.removeItem(`${KEY}.${orgRef}`);
  } catch {
    // Nothing to forget.
  }
}
